import { useEffect, useLayoutEffect } from 'react';
import { useLang } from './i18n';
import {
  mainToolWheelTitle,
  resolveMainToolContent,
  type MainToolId,
} from '../data/mainToolI18n';

const FAQ_ID = 'faq-jsonld';
const HOWTO_ID = 'howto-jsonld';
const APP_ID = 'app-jsonld';
const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

function installJsonLd(id: string, value: unknown) {
  document.getElementById(id)?.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(value).replace(/<\//g, '<\\/');
  document.head.appendChild(script);
}

function removeToolJsonLd() {
  document.getElementById(FAQ_ID)?.remove();
  document.getElementById(HOWTO_ID)?.remove();
  document.getElementById(APP_ID)?.remove();
}

/** Keep localized main-tool FAQ and HowTo schema correct after SPA navigation. */
export function useMainToolStructuredData(tool: MainToolId | null, enabled: boolean) {
  const { lang, t } = useLang();

  useClientLayoutEffect(() => {
    const canonicalHref = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href;
    const canonicalPath = canonicalHref ? new URL(canonicalHref, window.location.origin).pathname : '';
    const currentPath = window.location.pathname.endsWith('/')
      ? window.location.pathname
      : `${window.location.pathname}/`;
    const normalizedCanonical = canonicalPath && !canonicalPath.endsWith('/')
      ? `${canonicalPath}/`
      : canonicalPath;
    const spaNavigation = Boolean(normalizedCanonical && normalizedCanonical !== currentPath);

    // Preserve the richer English schema baked into a direct-load page. On an
    // English SPA transition, however, refresh it so the previous route's
    // entities never remain in the document head. A localized page always uses
    // this hook because its visible FAQ/HowTo copy comes from the same source.
    const shouldInstall = enabled || (lang === 'en' && spaNavigation);
    if (!tool || !shouldInstall) {
      if (spaNavigation) removeToolJsonLd();
      // Direct-load English pages keep their richer prerendered entities, but
      // removing them on unmount prevents those entities leaking into a later
      // route that does not manage tool schema itself.
      if (tool && lang === 'en') return removeToolJsonLd;
      return;
    }
    const content = resolveMainToolContent(tool, t);
    const pageUrl = `${window.location.origin}${currentPath}`;

    installJsonLd(FAQ_ID, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: lang,
      mainEntity: content.faqs.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    });

    installJsonLd(HOWTO_ID, {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      inLanguage: lang,
      name: content.howTitle,
      description: content.intro,
      tool: [{ '@type': 'HowToTool', name: 'TheNameWheel' }],
      step: content.steps.map((step, index) => ({
        '@type': 'HowToStep',
        name: String(index + 1),
        text: step,
        url: lang === 'en' ? pageUrl : `${pageUrl}#localized-step-${index + 1}`,
      })),
    });

    installJsonLd(APP_ID, {
      '@context': 'https://schema.org',
      // <!-- SEO: preserve the homepage WebApplication entity during navigation. -->
      '@type': 'WebApplication',
      name: tool === 'home' ? 'Name Wheel' : mainToolWheelTitle(content.h1),
      description: content.intro,
      url: pageUrl,
      inLanguage: lang,
      applicationCategory: tool === 'classroom' ? 'EducationalApplication' : tool === 'home' ? 'UtilityApplication' : 'UtilitiesApplication',
      operatingSystem: 'Any',
      browserRequirements: 'Requires JavaScript',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      publisher: { '@id': `${window.location.origin}/#organization` },
      isPartOf: { '@id': `${window.location.origin}/#website` },
    });

    return removeToolJsonLd;
  }, [enabled, lang, t, tool]);
}
